import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fab,
  Tooltip
} from '@mui/material'
import ChatIcon from '@mui/icons-material/Chat'
import { AuthContext } from 'src/context/auth/authContext'
import { labels } from 'src/labels/main_labels'
import { getAssistantChatUri, openAssistantChat } from 'src/services/assistant.services'

// Tras abrir el chat una vez, el botón lleva directo a Google Chat sin pasar por el diálogo.
const OPENED_KEY = 'assistantChatOpened'
const readOpened = (): boolean => {
  try {
    return localStorage.getItem(OPENED_KEY) === '1'
  } catch {
    return false
  }
}
const saveOpened = (): void => {
  try {
    localStorage.setItem(OPENED_KEY, '1')
  } catch {
    // Sin storage: el diálogo seguirá apareciendo, no pasa nada
  }
}

// Botón flotante para hablar con el asistente de citas por Google Chat. La primera vez
// explica el aviso de spam que mostrará Google y crea el chat en segundo plano.
export default function AssistantChatButton(): JSX.Element {
  const { userData, language } = useContext(AuthContext)
  const [chatUri, setChatUri] = useState<string | null>(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [preparing, setPreparing] = useState(false)
  const [error, setError] = useState(false)
  const [opened, setOpened] = useState(readOpened)

  useEffect(() => {
    if (!userData?.id) return
    getAssistantChatUri(userData.id)
      .then(setChatUri)
      .catch(() => setChatUri(null))
  }, [userData?.id])

  const prepareChat = () => {
    setPreparing(true)
    setError(false)
    openAssistantChat()
      .then(setChatUri)
      .catch(() => setError(true))
      .finally(() => setPreparing(false))
  }

  const handleFabClick = () => {
    setOpenDialog(true)
    if (!chatUri && !preparing) prepareChat()
  }

  const handleOpenChat = () => {
    saveOpened()
    setOpened(true)
    setOpenDialog(false)
  }

  const fabSx = {
    position: 'fixed',
    right: 24,
    // Por encima de la barra de paginación fija de las tablas (52px, 104px en móvil con filtros)
    bottom: { xs: 120, md: 72 },
    zIndex: 1200
  }

  return (
    <>
      <Tooltip title={labels[language].assistantChatTooltip} placement="left">
        {chatUri && opened ? (
          <Fab color="primary" sx={fabSx} component="a" href={chatUri} target="_blank" rel="noopener noreferrer">
            <ChatIcon />
          </Fab>
        ) : (
          <Fab color="primary" sx={fabSx} onClick={handleFabClick}>
            <ChatIcon />
          </Fab>
        )}
      </Tooltip>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>{labels[language].assistantChatTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{labels[language].assistantChatText}</DialogContentText>
          <Alert severity="info" sx={{ mb: 2 }}>
            {labels[language].assistantChatSpamHint}
          </Alert>
          {preparing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <DialogContentText component="span">{labels[language].assistantChatPreparing}</DialogContentText>
            </Box>
          )}
          {error && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={prepareChat}>
                  {labels[language].assistantChatRetry}
                </Button>
              }
            >
              {labels[language].assistantChatError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{labels[language].cancel}</Button>
          <Button
            variant="contained"
            disabled={!chatUri}
            component="a"
            href={chatUri || undefined}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleOpenChat}
          >
            {labels[language].assistantChatOpen}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
